import { useEffect, useState } from 'react';
import { imageRecognition } from '~libs';
import {
  CallbackCamera,
  ErrorPictureResponse,
  SuccessPictureResponse,
  SupportPictureResponse,
  DocumentCameraType,
  DocumentCameraTypes,
  UnicoConfig,
} from 'unico-webframe';
import { useNavigate } from 'react-router-dom';
import { CameraPlaceholder, PhotoConfirmation } from '~components';
import { useRequest } from 'ahooks';
import { Representative, Spouse } from '~services';
import { Param } from './IdentityBackCamera.types';
import { Response } from '~types/getUserData';
import { useTransshipment } from '~contexts/TranshipmentContext';
import { useLayout } from '~hooks';
import * as amplitude from '@amplitude/analytics-browser';
import { unicoConfig } from '~libs/imageRecognition';

function IdentityBackCamera({
  onClickCloseCamera,
}: {
  onClickCloseCamera: () => void;
}) {
  const transshipment = useTransshipment();
  const [showPhotoConfirmation, setShowPhotoConfirmation] = useState(false);
  const [base64Image, setBase64Image] = useState('');
  const { setLayout } = useLayout();
  const navigate = useNavigate();

  const request =
    transshipment.type === 'spouse'
      ? Spouse.addDocument
      : Representative.addDocument;

  const {
    runAsync: addDocument,
    error,
    loading: isLoadingAddDocument,
  } = useRequest<Response, Param>(request, {
    onSuccess: successAddDocument,
    manual: true,
  });

  async function successAddDocument() {
    if (transshipment.type === 'representative') {
      amplitude.track({
        event_type: 'documentation_id_sent',
        event_properties: {
          CPF_representatives: transshipment.data.cpf,
        },
      });
    }

    transshipment.nextStep();
  }

  useEffect(() => {
    if (error) throw error;
  }, [error]);

  const callbacks: CallbackCamera = {
    on: {
      success: function (response: SuccessPictureResponse) {
        setBase64Image(response.base64);
        setShowPhotoConfirmation(true);
      },
      error: function (error: ErrorPictureResponse) {},
      support: function (error: SupportPictureResponse) {},
    },
  };

  async function handleSuccess() {
    await addDocument(transshipment?.id, {
      file: base64Image,
      type: 'rg-back',
    });

    setLayout({ disabledLayout: false, header: {} });
  }

  const openDocumentCamera = async (
    jsonPath: UnicoConfig,
    cameraType: DocumentCameraType
  ) => {
    try {
      const { open } = await imageRecognition.prepareDocumentCamera(
        jsonPath,
        cameraType
      );

      open(callbacks);
    } catch (error) {
      navigate('/web/internet');
    }
  };

  function openCamera() {
    openDocumentCamera(
      unicoConfig,
      DocumentCameraTypes.OTHERS('Foto do verso do RG')
    );
  }

  function takeAnotherPhoto() {
    setShowPhotoConfirmation(false);
    openCamera();
  }

  useEffect(() => {
    setLayout({ disabledLayout: true, header: {} });
    openCamera();
  }, []);

  return showPhotoConfirmation ? (
    <PhotoConfirmation
      base64Image={base64Image}
      onClickIsGood={handleSuccess}
      onClickTakeAnother={takeAnotherPhoto}
      rotateImage
      isLoadingConfirmation={isLoadingAddDocument}
    />
  ) : (
    <CameraPlaceholder onClickClose={onClickCloseCamera} />
  );
}

export default IdentityBackCamera;
